import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * usePullToRefresh — wire up a scrollable container for native-feeling pull-to-refresh.
 *
 * Returns: { containerRef, pullDistance, isRefreshing, trigger }
 *
 * Usage:
 *   const { containerRef, pullDistance, isRefreshing } = usePullToRefresh(async () => {
 *     await loadData();
 *   });
 *   <div ref={containerRef}>...</div>
 *   <PullToRefreshIndicator distance={pullDistance} refreshing={isRefreshing} />
 */
export function usePullToRefresh(onRefresh, { threshold = 70, maxDistance = 120 } = {}) {
  const containerRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);

  const trigger = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [onRefresh, isRefreshing]);

  useEffect(() => {
    const el = containerRef.current || window;
    const getScrollTop = () => {
      if (el === window) return window.scrollY;
      return el.scrollTop;
    };

    const onTouchStart = (e) => {
      if (getScrollTop() > 0 || isRefreshing) return;
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    };

    const onTouchMove = (e) => {
      if (!pulling.current) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta <= 0) {
        setPullDistance(0);
        return;
      }
      // Rubber-band feel: dampen after threshold
      const damped = delta < threshold
        ? delta
        : threshold + (delta - threshold) * 0.35;
      setPullDistance(Math.min(damped, maxDistance));
    };

    const onTouchEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pullDistance >= threshold) {
        trigger();
      } else {
        setPullDistance(0);
      }
    };

    const target = el === window ? window : el;
    target.addEventListener('touchstart', onTouchStart, { passive: true });
    target.addEventListener('touchmove', onTouchMove, { passive: true });
    target.addEventListener('touchend', onTouchEnd);
    target.addEventListener('touchcancel', onTouchEnd);
    return () => {
      target.removeEventListener('touchstart', onTouchStart);
      target.removeEventListener('touchmove', onTouchMove);
      target.removeEventListener('touchend', onTouchEnd);
      target.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [pullDistance, threshold, maxDistance, trigger, isRefreshing]);

  return { containerRef, pullDistance, isRefreshing, trigger };
}

/** Visual indicator for pull-to-refresh. Renders at the top of the scroll area. */
export function PullToRefreshIndicator({ distance, refreshing, threshold = 70 }) {
  const visible = distance > 0 || refreshing;
  const rotation = Math.min((distance / threshold) * 360, 360);
  return (
    <div
      data-testid="ptr-indicator"
      aria-hidden="true"
      className="flex items-center justify-center overflow-hidden transition-all"
      style={{
        height: refreshing ? 48 : Math.min(distance, 80),
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        className={`w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full ${refreshing ? 'animate-spin' : ''}`}
        style={{ transform: refreshing ? undefined : `rotate(${rotation}deg)` }}
      />
    </div>
  );
}
